/**
 * BoardCarousel — mobile-first single-realm-in-focus layout. The 7
 * orbital realms are stacked along a horizontal axis: lower-tier realms
 * to the left, higher-tier to the right. The active realm is in the
 * foreground; neighbours are ghosted and tucked behind. Swipe (or
 * arrow buttons) navigates through tiers.
 *
 * Why this exists: the 7-realm grid breaks down on narrow viewports.
 * The competing client doesn't address mobile at all. A single-realm
 * focus mode with swipe is the natural mobile-native answer; on
 * desktop it's still useful for tournament spectating where focus
 * matters more than overview.
 */

import { type ReactElement, useState } from 'react';
import type { CardData, PlayerId, PublicTableState, RealmName, TableAction } from '../types.js';
import { BoardModeSwitcher } from './BoardModeSwitcher.js';
import { Card } from './Card.js';

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  opponent: PlayerId;
  cards: ReadonlyMap<string, CardData>;
  imageUrl: (cardId: string) => string;
  onSeizeStone: (idx: number, player: PlayerId | null) => void;
  dispatchAction: (a: TableAction) => void;
};

export function BoardCarousel({
  state,
  viewer,
  opponent,
  cards,
  imageUrl,
  onSeizeStone,
}: Props): ReactElement {
  const [activeIdx, setActiveIdx] = useState(3); // start mid-tier (4th realm)
  const realms = state.realms;
  const round = state.round ?? 0;
  const isMyTurn = state.activePlayer === viewer;

  const next = () => setActiveIdx((i) => Math.min(realms.length - 1, i + 1));
  const prev = () => setActiveIdx((i) => Math.max(0, i - 1));

  const active = realms[activeIdx];
  if (!active) return <div className="p-4 text-rose-400">No realm to display.</div>;

  // Compute neighbour previews — up to 3 on each side, ghosted by distance.
  const previews = realms
    .map((r, i) => ({ realm: r, i, distance: i - activeIdx }))
    .filter((x) => x.distance !== 0 && Math.abs(x.distance) <= 3);

  return (
    <div className="flex h-screen w-screen flex-col bg-cosmos-950 text-cosmos-100">
      {/* HUD strip */}
      <header className="flex items-center justify-between border-cosmos-800 border-b bg-cosmos-900/80 px-3 py-2">
        <div className="text-xs text-amber-300 font-mono">
          R{round}/7 · {isMyTurn ? 'Your Turn' : "Opp's turn"}
        </div>
        <BoardModeSwitcher />
      </header>

      {/* Carousel viewport */}
      <main className="relative flex flex-1 items-center justify-center overflow-hidden">
        {/* Ghost stack — left side (lower tiers) */}
        <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-3 px-3">
          {previews
            .filter((p) => p.distance < 0)
            .reverse()
            .map((p) => (
              <RealmGhost
                key={p.i}
                realm={p.realm}
                distance={Math.abs(p.distance)}
                onClick={() => setActiveIdx(p.i)}
                side="left"
              />
            ))}
        </div>

        {/* Active realm — full-bleed */}
        <section className="z-10 flex h-full w-full max-w-3xl flex-col items-center justify-center px-4">
          <h2 className="mb-2 text-center font-display text-2xl text-amber-300">
            {active.name}
            <span className="ml-2 text-xs text-cosmos-400">
              tier {activeIdx + 1}/{realms.length}
            </span>
          </h2>
          <div className="flex h-2/3 w-full flex-col items-center justify-center rounded-md border border-cosmos-700 bg-cosmos-900/60 p-4">
            {active.placements.length === 0 ? (
              <p className="text-cosmos-500 italic">No cards in this realm yet.</p>
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                {active.placements.map((p) => (
                  <Card
                    key={`${p.cardId}-${p.x}-${p.y}`}
                    cardId={p.cardId as string}
                    card={cards.get(p.cardId as string)}
                    imageUrl={imageUrl(p.cardId as string)}
                    placement={p}
                    faceDown={p.faceDown}
                    tapped={p.tapped}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-cosmos-300">
            <span>
              Stone: <strong>{active.stone ?? 'uncontested'}</strong>
            </span>
            <button
              type="button"
              onClick={() => onSeizeStone(activeIdx, viewer)}
              className="rounded bg-amber-700 px-2 py-0.5 text-xs hover:bg-amber-600"
            >
              Seize stone
            </button>
          </div>
        </section>

        {/* Ghost stack — right side (higher tiers) */}
        <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-3 px-3">
          {previews
            .filter((p) => p.distance > 0)
            .map((p) => (
              <RealmGhost
                key={p.i}
                realm={p.realm}
                distance={p.distance}
                onClick={() => setActiveIdx(p.i)}
                side="right"
              />
            ))}
        </div>

        {/* Nav buttons (also act as touch targets for swipe-equivalents) */}
        <button
          type="button"
          onClick={prev}
          disabled={activeIdx === 0}
          className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-cosmos-800/80 px-3 py-2 text-lg text-cosmos-100 shadow disabled:opacity-30 hover:bg-cosmos-700"
          aria-label="Previous realm"
        >
          ‹
        </button>
        <button
          type="button"
          onClick={next}
          disabled={activeIdx === realms.length - 1}
          className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-cosmos-800/80 px-3 py-2 text-lg text-cosmos-100 shadow disabled:opacity-30 hover:bg-cosmos-700"
          aria-label="Next realm"
        >
          ›
        </button>
      </main>

      {/* Tier breadcrumb */}
      <footer className="flex justify-center gap-1 border-cosmos-800 border-t bg-cosmos-900/80 py-2">
        {realms.map((r, i) => (
          <button
            key={r.name}
            type="button"
            onClick={() => setActiveIdx(i)}
            className={`h-2 w-6 rounded ${i === activeIdx ? 'bg-amber-400' : 'bg-cosmos-700 hover:bg-cosmos-600'}`}
            title={r.name}
            aria-label={`Jump to ${r.name}`}
          />
        ))}
      </footer>
    </div>
  );
}

function RealmGhost({
  realm,
  distance,
  side,
  onClick,
}: {
  realm: { name: RealmName; placements: readonly { owner: PlayerId }[]; stone: PlayerId | null };
  distance: number;
  side: 'left' | 'right';
  onClick: () => void;
}): ReactElement {
  // Further realms = more transparent + smaller. Distance 1..3 maps to
  // opacity 0.45..0.15 and scale 0.9..0.65.
  const opacity = Math.max(0.1, 0.55 - distance * 0.13);
  const scale = Math.max(0.55, 0.95 - distance * 0.13);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ opacity, transform: `scale(${scale})` }}
      className={`pointer-events-auto rounded-md border border-cosmos-700 bg-cosmos-900/60 px-3 py-2 text-left text-xs text-cosmos-200 transition hover:opacity-80 ${
        side === 'left' ? '-translate-x-1' : 'translate-x-1'
      }`}
    >
      <div className="font-semibold text-cosmos-100">{realm.name}</div>
      <div className="text-[10px] text-cosmos-400">
        {realm.placements.length} cards · stone: {realm.stone ?? '—'}
      </div>
    </button>
  );
}
